import * as React from 'react';
import { AuthState } from '../auth/auth.types';
import TextField from 'material-ui/TextField';
import RaisedButton from 'material-ui/RaisedButton';
import { isValidName, isValidPhone } from '../util/Validation';
import { pickAccount } from './Util';
import RefreshIndicator from 'material-ui/RefreshIndicator';
import Phone from 'react-phone-number-input';
import { isAdminAccount, isImpersonating } from '../admin/Util';
import { AdminState } from '../admin/admin.types';
import { isCapsLockPressed } from '../util/capslock.check.util';
import CommonFeatures from '../commons/CommonFeatures';
import { isAuthTokenExpired } from '../commons/AuthTokenValidator';
import SectionPanel from '../util/SectionPanelComponent';
import { BusinessState } from 'src/business/business.types';

const NAME_ERROR_MESSAGE = 'Only alphabet, dash, and/or space are allowed in name';
const PHONE_NUMBER_ERROR_MESSAGE = 'Invalid Phone Number';
const CONFIRM_NEW_PASSWORD_ERROR_MESSAGE = 'Confirm new password has to match';

type Props = {
  readonly authState: AuthState
  readonly adminState: AdminState
  readonly businessState: BusinessState
  readonly updateAccount: (
    firstName: string,
    lastName: string,
    email: string,
    password: string | undefined,
    phone: string,
    isImpersonating: boolean
  ) => Promise<Response>
  readonly toAdminDashboard: () => void
};

type States = {
  readonly firstName: string
  readonly editedFirstName: string
  readonly editedFirstNameErrorMessage: string
  readonly lastName: string
  readonly editedLastName: string
  readonly editedLastNameErrorMessage: string
  readonly phoneNumber: string
  readonly editedPhoneNumber: string
  readonly editedPhoneNumberErrorMessage: string
  readonly email: string
  readonly newPassword: string | undefined
  readonly confirmNewPassword: string | undefined
  readonly confirmNewPasswordErrorMessage: string
  readonly errorExists: boolean
  readonly updated: boolean
  readonly loading: boolean
  readonly capsLockWarningMessage: string
};

class AccountMyInfoComponent extends React.Component<Props, States> {

  constructor(props: Props) {
    super(props);

    this.state = {
      firstName: pickAccount(props.authState, props.adminState).firstName,
      editedFirstName: pickAccount(props.authState, props.adminState).firstName,
      editedFirstNameErrorMessage: '',
      lastName: pickAccount(props.authState, props.adminState).lastName,
      editedLastName: pickAccount(props.authState, props.adminState).lastName,
      editedLastNameErrorMessage: '',
      phoneNumber: pickAccount(props.authState, props.adminState).phone,
      editedPhoneNumber: pickAccount(props.authState, props.adminState).phone,
      editedPhoneNumberErrorMessage: '',
      newPassword: '',
      confirmNewPassword: '',
      confirmNewPasswordErrorMessage: '',
      email: pickAccount(props.authState, props.adminState).email,
      errorExists: false,
      updated: false,
      loading: false,
      capsLockWarningMessage: ''
    };
    
    this.isFieldEdited = this.isFieldEdited.bind(this);
    this.handleFirstNameChange = this.handleFirstNameChange.bind(this);
    this.handleLasttNameChange = this.handleLasttNameChange.bind(this);
    this.handlePhoneNumberChange = this.handlePhoneNumberChange.bind(this);
    this.handleNewPasswordChange = this.handleNewPasswordChange.bind(this);
    this.handleConfirmNewPasswordChange = this.handleConfirmNewPasswordChange.bind(this);
    this.isFieldEdited = this.isFieldEdited.bind(this);
    this.doesConfirmNewPasswordMatch = this.doesConfirmNewPasswordMatch.bind(this);
    this.doesNewPasswordMatch = this.doesNewPasswordMatch.bind(this);
    this.onUpdateClick = this.onUpdateClick.bind(this);
    this.onKeyPress = this.onKeyPress.bind(this);
  }

  componentDidMount() {
    const { authState, toAdminDashboard, adminState } = this.props;

    if (isAdminAccount(authState) && !isImpersonating(adminState)) {
      return toAdminDashboard();
    }
  }

  componentWillReceiveProps(nextProps: Props) {
    if (this.props !== nextProps && !isAuthTokenExpired() && nextProps.authState.statusCode === 200) {
      this.setState({
        firstName: pickAccount(nextProps.authState, nextProps.adminState).firstName,
        editedFirstName: pickAccount(nextProps.authState, nextProps.adminState).firstName,
        editedFirstNameErrorMessage: '',
        lastName: pickAccount(nextProps.authState, nextProps.adminState).lastName,
        editedLastName: pickAccount(nextProps.authState, nextProps.adminState).lastName,
        editedLastNameErrorMessage: '',
        phoneNumber: pickAccount(nextProps.authState, nextProps.adminState).phone,
        editedPhoneNumber: pickAccount(nextProps.authState, nextProps.adminState).phone,
        editedPhoneNumberErrorMessage: '',
        newPassword: '',
        confirmNewPassword: '',
        confirmNewPasswordErrorMessage: '',
        email: pickAccount(nextProps.authState, nextProps.adminState).email,
        errorExists: false,
        capsLockWarningMessage: ''
      });
    }
  }

  render() {
    const {
      editedFirstName, editedLastName, editedPhoneNumber, newPassword, confirmNewPassword, email,
      errorExists, editedPhoneNumberErrorMessage, editedFirstNameErrorMessage, updated, loading,
      editedLastNameErrorMessage, confirmNewPasswordErrorMessage, capsLockWarningMessage
    } = this.state;

    const { authState } = this.props;

    return(
      <CommonFeatures
        authState={this.props.authState}
        businessState={this.props.businessState}
      >
        <div className="page-header-row row mt-2">
          <div className="col-auto">
            <h3>My Information</h3>
          </div>
        </div>
        <div className="row ma2">
          <div className="col-lg-1 col-md-hidden col-sm-hidden" />
          <div className="col-lg-10 col-md-12 col-sm-12">
            <SectionPanel
              className="row mt-5 mb-5"
              title="General Information"
              titleAlign="center"
            > 
              <div className="col-lg-12 col-md-12 col-sm-12">
                <div className="row p-4">
                  <div className="col-lg-4 col-md-12 col-sm-12">
                    <TextField
                      fullWidth={true}
                      disabled={true}
                      floatingLabelText="Email (Display Only)"
                      value={email}
                    />
                  </div>
                  <div className="col-lg-4 col-md-6 col-sm-12">
                    <TextField
                      fullWidth={true}
                      type="password"
                      hintText="Enter New Password"
                      floatingLabelText="New Password"
                      value={newPassword}
                      onChange={this.handleNewPasswordChange}
                      errorText={confirmNewPasswordErrorMessage}
                      onKeyPress={this.onKeyPress}
                    />
                  </div>
                  <div className="col-lg-4 col-md-6 col-sm-12">
                    <TextField
                      fullWidth={true}
                      type="password"
                      hintText="Confirm New Password"
                      floatingLabelText="Confirm New Password"
                      value={confirmNewPassword}
                      onChange={this.handleConfirmNewPasswordChange}
                      onKeyPress={this.onKeyPress}
                    />
                  </div>
                </div>
                <div className="row p-4">
                  <div className="col-lg-4 col-md-6 col-sm-12">
                    <TextField
                      fullWidth={true}
                      className="material-input-required-red"
                      hintText="Enter First Name"
                      floatingLabelText="First Name"
                      value={editedFirstName}
                      onChange={this.handleFirstNameChange}
                      errorText={editedFirstNameErrorMessage}
                    />
                  </div>
                  <div className="col-lg-4 col-md-6 col-sm-12">
                    <TextField
                      fullWidth={true}
                      className="material-input-required-red"
                      hintText="Enter Last Name"
                      floatingLabelText="Last Name"
                      value={editedLastName}
                      onChange={this.handleLasttNameChange}
                      errorText={editedLastNameErrorMessage}
                    />
                  </div>
                  <div className="col-lg-4 col-md-12 col-sm-12">
                    <TextField
                      fullWidth={true}
                      className="material-input-required-red"
                      hintText="Enter Phone Number"
                      floatingLabelText="Phone Number"
                      errorText={editedPhoneNumberErrorMessage}
                    >
                      <Phone
                        countries={['CA']}
                        value={editedPhoneNumber}
                        onChange={this.handlePhoneNumberChange}
                        convertToNational={true}
                        inputStyle={{ backgroundColor: 'transparent', borderBottom: '0px'}}
                      />
                    </TextField>
                  </div>
                </div>
                <div className="row p-4">
                  <div className="col-lg-10 col-md-12 text-center green">
                    {
                      updated && authState.statusCode === 200 ?
                      <div className="animated fadeIn">
                        Update Successful
                      </div> :
                      ''
                    }
                  </div>
                  <div className="col-lg-10 col-md-12 text-center red">
                    {capsLockWarningMessage}
                  </div>
                  <div className="col-lg-2 col-md-12">
                    {
                      loading ?
                      <RefreshIndicator
                        size={40}
                        left={10}
                        top={0}
                        status="loading"
                        style={{
                          display: 'inline-block',
                          position: 'relative',
                          float: 'right'
                        }}
                      /> :
                      <RaisedButton
                        label="UPDATE"
                        primary={true} 
                        disabled={errorExists || !this.isFieldEdited(this.state)}
                        onClick={this.onUpdateClick}
                        className="float-right"
                      />
                    }
                  </div>
                </div>
              </div>
            </SectionPanel>
            <div className="col-lg-1 col-md-hidden col-sm-hidden" />
          </div>
        </div>
      </CommonFeatures>
    );
  }

  private onUpdateClick = (): void => {
    const { updateAccount, adminState } = this.props;
    const { editedFirstName, editedLastName, email, editedPhoneNumber, confirmNewPassword } = this.state;

    this.setState(
      {loading: !isAuthTokenExpired()},
      () => updateAccount(
        editedFirstName,
        editedLastName,
        email,
        confirmNewPassword ? confirmNewPassword : undefined,
        editedPhoneNumber,
        isImpersonating(adminState)
      )
      .then(response => 
        this.setState({
          updated: true,
          loading: false,
          newPassword: '',
          confirmNewPassword: ''
        })
      )
    );
  }

  private isFieldEdited = (states: States): boolean => {
    const { firstName, editedFirstName, lastName, editedLastName, phoneNumber, editedPhoneNumber,
      newPassword, confirmNewPassword } = states;

    return firstName !== editedFirstName || lastName !== editedLastName || phoneNumber !== editedPhoneNumber ||
      newPassword !== '' || confirmNewPassword !== '';
  }

  private handleFirstNameChange = (event: React.FormEvent<HTMLInputElement>, newValue: string): void =>
    this.setState({
      editedFirstName: newValue,
      editedFirstNameErrorMessage: isValidName(newValue) ? '' : NAME_ERROR_MESSAGE,
      errorExists: !isValidName(newValue),
      updated: false
    })

  private handleLasttNameChange = (event: React.FormEvent<HTMLInputElement>, newValue: string): void =>
    this.setState({
      editedLastName: newValue,
      editedLastNameErrorMessage: isValidName(newValue) ? '' : NAME_ERROR_MESSAGE,
      errorExists: !isValidName(newValue),
      updated: false
    })

  private handlePhoneNumberChange = (newPhone: string): void =>
    this.setState({
      editedPhoneNumber: newPhone,
      editedPhoneNumberErrorMessage: isValidPhone(newPhone, PHONE_NUMBER_ERROR_MESSAGE),
      errorExists: isValidPhone(newPhone, PHONE_NUMBER_ERROR_MESSAGE) !== '',
      updated: false
    })

  private handleNewPasswordChange = (event: React.FormEvent<HTMLInputElement>, newValue: string): void =>
    this.setState({
      newPassword: newValue,
      confirmNewPasswordErrorMessage:
        this.doesNewPasswordMatch(newValue) ? '' : CONFIRM_NEW_PASSWORD_ERROR_MESSAGE,
      errorExists: !this.doesNewPasswordMatch(newValue),
      updated: false
    })

  private handleConfirmNewPasswordChange = (event: React.FormEvent<HTMLInputElement>, newValue: string): void =>
    this.setState({
      confirmNewPassword: newValue,
      confirmNewPasswordErrorMessage:
        (this.doesConfirmNewPasswordMatch(newValue) ? '' : CONFIRM_NEW_PASSWORD_ERROR_MESSAGE),
      errorExists: !this.doesConfirmNewPasswordMatch(newValue),
      updated: false
    })

  private doesNewPasswordMatch = (newValue: string): boolean => newValue === this.state.confirmNewPassword;
  private doesConfirmNewPasswordMatch = (newValue: string): boolean => this.state.newPassword === newValue;

  private onKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) =>
    this.setState({capsLockWarningMessage: isCapsLockPressed(event) ? 'CapsLock is on' : ''})
}

export default AccountMyInfoComponent;



// WEBPACK FOOTER //
// ./src/account/MyInfoComponent.tsx